/**
 * wallet.js — Play-money balance for Sky-Drop's staked matches.
 *
 * A player puts up a stake before a ranked series against the AI, and either
 * loses it or is paid out at the tier's odds. This is PLAY money: it is earned
 * and spent entirely against the computer, and it buys cosmetics. It is
 * deliberately NOT the same balance as the crypto one planned later — the two
 * never convert, which is what keeps this side of the economy from needing a
 * server to referee it.
 *
 * That is also why the balance lives in localStorage alongside the scores
 * rather than on the worker. A player can edit it, and the worst thing that
 * buys them is a skin in their own copy of the game. Putting it server-side
 * would mean accounts — the first persistent identity this game has ever had —
 * and none of that is justified by cosmetics. When the crypto balance arrives
 * it brings its own identity, and this one can be imported once.
 *
 * Every accessor fails soft, for the same reason score.js does: a wallet that
 * cannot be read must never stop somebody playing.
 */

import { isDifficulty } from './ai';

const STORAGE_KEY = 'skydrop_wallet';

/**
 * What a new player starts with.
 *
 * Enough to lose several times at the middle tier and still be able to play,
 * because a staking mode that can strand somebody at zero on their first
 * evening is a mode they never come back to. See topUp for the floor that
 * catches them if they do.
 */
export const STARTING_BALANCE = 500;

/**
 * Handed out when a broke player asks for more, and the ceiling that qualifies
 * them. Play money exists to make a match feel like it is for something, not to
 * be scarce — being unable to play at all is the one outcome worth designing
 * away.
 */
export const TOP_UP_AMOUNT = 250;
export const TOP_UP_CEILING = 50;

/**
 * Odds per AI tier: what a winning stake is multiplied by, on top of getting
 * the stake itself back. Losing costs the stake and nothing more.
 *
 * Weighted hard toward the top tiers on purpose. Beating Easy is not an
 * achievement and should not pay like one; x-hard is meant to be the reason to
 * carry a balance at all. These are odds against a KNOWN opponent rather than a
 * fair coin, so they are a design lever, not a probability — tune them by how
 * the tiers actually play, not by arithmetic.
 */
const ODDS = { easy: 0.25, medium: 1, hard: 2, xhard: 4 };

/** Smallest and largest a player may put up on one series. */
export const MIN_STAKE = 0;
export const MAX_STAKE = 1000;

/**
 * What a winning series pays out in total, stake included.
 *
 * @param {number} stake      Amount put up.
 * @param {string} difficulty AI tier id.
 * @returns {number} total credited on a win; 0 for a zero stake.
 */
export function payoutFor(stake, difficulty) {
  const tier = isDifficulty(difficulty) ? difficulty : 'medium';
  const amount = Math.max(0, Math.floor(stake));
  return amount + Math.round(amount * ODDS[tier]);
}

/**
 * The profit half of a payout — what the player actually gains on a win.
 * @param {number} stake
 * @param {string} difficulty
 * @returns {number}
 */
export function winningsFor(stake, difficulty) {
  return payoutFor(stake, difficulty) - Math.max(0, Math.floor(stake));
}

/** Read the stored balance, or the starting float if there isn't one yet. */
export function getBalance() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return STARTING_BALANCE;
    const parsed = JSON.parse(raw);
    const value = parsed && typeof parsed === 'object' ? parsed.balance : parsed;
    // A corrupt or negative stored value reads as a fresh wallet rather than as
    // a debt the player can never clear.
    return Number.isFinite(value) && value >= 0
      ? Math.floor(value)
      : STARTING_BALANCE;
  } catch (err) {
    return STARTING_BALANCE;
  }
}

/**
 * Write the balance back. Silently ignores storage failures, exactly as the
 * score does — an unwritable wallet is not worth interrupting play for.
 * @param {number} balance
 * @returns {number} the balance as stored.
 */
function save(balance) {
  const value = Math.max(0, Math.floor(balance));
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ balance: value }));
  } catch (err) {
    // No-op.
  }
  return value;
}

/**
 * Can this stake be covered right now?
 * @param {number} stake
 * @returns {boolean}
 */
export function canCover(stake) {
  const amount = Math.max(0, Math.floor(stake));
  return amount <= getBalance() && amount <= MAX_STAKE;
}

/**
 * Take a stake out of the balance at the start of a series.
 *
 * Debited up front rather than settled at the end, so the money is visibly
 * committed for the whole series and cannot be spent from another screen while
 * a match it is riding on is still running.
 *
 * @param {number} stake
 * @returns {number|null} the new balance, or null if it could not be covered.
 */
export function placeStake(stake) {
  const amount = Math.max(0, Math.floor(stake));
  if (!canCover(amount)) return null;
  return save(getBalance() - amount);
}

/**
 * Settle a finished series.
 *
 * @param {number} stake      What was put up (already debited by placeStake).
 * @param {string} difficulty AI tier the series was played at.
 * @param {boolean} won       Did the player take the series?
 * @returns {{balance: number, change: number}} new balance, and the swing
 *   relative to before the stake was placed.
 */
export function settleSeries(stake, difficulty, won) {
  const amount = Math.max(0, Math.floor(stake));
  if (!won) return { balance: getBalance(), change: -amount };
  const payout = payoutFor(amount, difficulty);
  return { balance: save(getBalance() + payout), change: payout - amount };
}

/**
 * Hand a broke player enough to keep going.
 *
 * @returns {number} the balance afterwards, unchanged if they were not broke.
 */
export function topUp() {
  const balance = getBalance();
  if (balance > TOP_UP_CEILING) return balance;
  return save(balance + TOP_UP_AMOUNT);
}

/** Wipe the wallet back to a fresh start. */
export function resetWallet() {
  return save(STARTING_BALANCE);
}
